import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../lib/LanguageContext";
import { api, whatsappLink } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import SEO, { breadcrumbSchema, hotelSchema } from "../components/SEO";

export default function Contact() {
    const { t, lang } = useLang();
    const { site } = useOutletContext() || {};
    const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
    const [busy, setBusy] = useState(false);

    const phone = site?.phone || "0682116731";
    const whatsapp = site?.whatsapp || "355682116731";
    const email = site?.email || "info@dearadhime.al";
    const address = lang === "al" ? site?.address_al : site?.address_en;
    const mapEmbed =
        site?.map_embed ||
        "https://www.google.com/maps?q=Radhim%C3%AB,Vlor%C3%AB,Albania&output=embed";

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) return;
        setBusy(true);
        try {
            await api.post("/contact", form);
            toast.success(t("send_success"));
            setForm({ name: "", email: "", phone: "", message: "" });
        } catch {
            toast.error(t("send_error"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <SEO
                title={lang === "al" ? "Kontakti" : "Contact"}
                description={lang === "al" ? "Na kontaktoni në Kompleksin Dea Radhimë — telefon 0682116731, WhatsApp, hartë dhe formular kontakti." : "Contact Kompleksi Dea Radhimë — phone 0682116731, WhatsApp, map and contact form."}
                path="/contact"
                schemas={[
                    hotelSchema({ site, rooms: [] }),
                    breadcrumbSchema([
                        { name: lang === "al" ? "Kreu" : "Home", url: "/" },
                        { name: lang === "al" ? "Kontakti" : "Contact", url: "/contact" },
                    ]),
                ]}
            />
            {/* Header */}
            <section className="relative h-[40vh] min-h-[300px] flex items-end overflow-hidden">
                <div className="absolute inset-0 bg-[#0F3A59]" />
                <div
                    className="absolute inset-0 bg-cover bg-center opacity-40"
                    style={{
                        backgroundImage: `url(https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&q=80&w=2000)`,
                    }}
                />
                <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pb-12 pt-32 w-full">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                    >
                        <span className="text-xs tracking-[0.3em] uppercase text-white/80">
                            {t("nav_contact")}
                        </span>
                        <h1 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-3">
                            {t("contact_us")}
                        </h1>
                        <p className="text-white/85 font-light text-lg mt-4 max-w-2xl">
                            {t("contact_sub")}
                        </p>
                    </motion.div>
                </div>
            </section>

            <section className="py-20 md:py-28 px-6 md:px-12 max-w-7xl mx-auto">
                <div className="grid md:grid-cols-2 gap-10 md:gap-20">
                    {/* Left: details */}
                    <motion.div
                        initial={{ opacity: 0, x: -30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                    >
                        <h2 className="font-serif text-3xl md:text-4xl text-[#0F3A59] tracking-tight">
                            {lang === "al" ? "Bisedoni me ne" : "Get in touch"}
                        </h2>
                        <p className="text-[#1C1C1C]/70 font-light mt-4">
                            {lang === "al"
                                ? "Na shkruani ose telefononi për rezervime, pyetje ose kërkesa speciale."
                                : "Reach out for reservations, inquiries, or special requests."}
                        </p>

                        <div className="mt-10 space-y-6">
                            <a
                                href={`tel:${phone}`}
                                data-testid="contact-phone"
                                className="flex items-start gap-4 group"
                            >
                                <div className="w-12 h-12 rounded-full bg-[#F4EFE6] flex items-center justify-center shrink-0 group-hover:bg-[#0F3A59] group-hover:text-white transition">
                                    <Phone size={18} strokeWidth={1.5} className="text-[#0F3A59] group-hover:text-white" />
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-[#1C1C1C]/60">
                                        {lang === "al" ? "Telefoni" : "Phone"}
                                    </div>
                                    <div className="font-serif text-xl text-[#0F3A59] mt-0.5">
                                        {phone}
                                    </div>
                                </div>
                            </a>

                            <a
                                href={whatsappLink(whatsapp, t("message_book"))}
                                target="_blank"
                                rel="noreferrer"
                                data-testid="contact-whatsapp"
                                className="flex items-start gap-4 group"
                            >
                                <div className="w-12 h-12 rounded-full bg-[#25D366]/10 flex items-center justify-center shrink-0 group-hover:bg-[#25D366] transition">
                                    <MessageCircle
                                        size={18}
                                        strokeWidth={1.5}
                                        className="text-[#25D366] group-hover:text-white"
                                    />
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-[#1C1C1C]/60">
                                        WhatsApp
                                    </div>
                                    <div className="font-serif text-xl text-[#0F3A59] mt-0.5">
                                        {lang === "al" ? "Na shkruani" : "Message us"}
                                    </div>
                                </div>
                            </a>

                            <a
                                href={`mailto:${email}`}
                                data-testid="contact-email"
                                className="flex items-start gap-4 group"
                            >
                                <div className="w-12 h-12 rounded-full bg-[#F4EFE6] flex items-center justify-center shrink-0 group-hover:bg-[#0F3A59] group-hover:text-white transition">
                                    <Mail size={18} strokeWidth={1.5} className="text-[#0F3A59] group-hover:text-white" />
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-[#1C1C1C]/60">
                                        Email
                                    </div>
                                    <div className="font-serif text-xl text-[#0F3A59] mt-0.5">
                                        {email}
                                    </div>
                                </div>
                            </a>

                            <div className="flex items-start gap-4">
                                <div className="w-12 h-12 rounded-full bg-[#F4EFE6] flex items-center justify-center shrink-0">
                                    <MapPin size={18} strokeWidth={1.5} className="text-[#0F3A59]" />
                                </div>
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-[#1C1C1C]/60">
                                        {lang === "al" ? "Adresa" : "Address"}
                                    </div>
                                    <div className="font-serif text-xl text-[#0F3A59] mt-0.5">
                                        {address}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Right: form */}
                    <motion.form
                        onSubmit={submit}
                        data-testid="contact-form"
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="bg-white border border-[#E5E0D8] rounded-2xl p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
                    >
                        <h3 className="font-serif text-2xl md:text-3xl text-[#0F3A59]">
                            {t("send_message")}
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                            <Input
                                placeholder={t("your_name")}
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                required
                                data-testid="contact-name"
                                className="h-12 rounded-lg border-[#E5E0D8]"
                            />
                            <Input
                                type="email"
                                placeholder={t("your_email")}
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                required
                                data-testid="contact-email-input"
                                className="h-12 rounded-lg border-[#E5E0D8]"
                            />
                        </div>
                        <Input
                            type="tel"
                            placeholder={t("your_phone")}
                            value={form.phone}
                            onChange={(e) => setForm({ ...form, phone: e.target.value })}
                            data-testid="contact-phone-input"
                            className="h-12 rounded-lg border-[#E5E0D8] mt-4"
                        />
                        <Textarea
                            placeholder={t("your_message")}
                            value={form.message}
                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                            required
                            rows={5}
                            data-testid="contact-message"
                            className="rounded-lg border-[#E5E0D8] mt-4 resize-none"
                        />
                        <Button
                            type="submit"
                            disabled={busy}
                            data-testid="contact-submit"
                            className="w-full mt-6 bg-[#0F3A59] hover:bg-[#1C5279] text-white h-12 rounded-full font-medium tracking-wide"
                        >
                            {busy ? t("loading") : t("send_message")}
                            <Send size={16} className="ml-2" />
                        </Button>
                    </motion.form>
                </div>
            </section>

            {/* Map */}
            <section className="px-6 md:px-12 pb-20">
                <div className="max-w-7xl mx-auto">
                    <h3 className="font-serif text-2xl md:text-3xl text-[#0F3A59] mb-6">
                        {t("find_us")}
                    </h3>
                    <div className="rounded-2xl overflow-hidden border border-[#E5E0D8] aspect-video md:aspect-[21/9] shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
                        <iframe
                            src={mapEmbed}
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="map"
                            data-testid="map-iframe"
                        />
                    </div>
                </div>
            </section>
        </div>
    );
}
